import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { href: '#about',        label: 'About' },
   { to: '/gallery',           label: 'gallery', isPage: true },
  { to: '/meet-the-team',  label: 'Meet the Team', isPage: true },
  { to: '/blog',           label: 'Blog', isPage: true },
];

export default function Navbar({ dark, toggleTheme }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const isHome = location.pathname === '/';

  const handleLink = (link) => {
    setMenuOpen(false);
    if (link.isPage) {
      navigate(link.to);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!isHome) {
      navigate('/');
      setTimeout(() => document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' }), 120);
    } else {
      document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleContact = () => {
    setMenuOpen(false);
    if (!isHome) {
      navigate('/');
      setTimeout(() => document.querySelector('#contact-cta')?.scrollIntoView({ behavior: 'smooth' }), 120);
    } else {
      document.querySelector('#contact-cta')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isActivePage = (link) => link.isPage && location.pathname === link.to;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-300
      ${dark ? 'bg-green-900/90 border-green-400/10 backdrop-blur-md' : 'bg-white/92 border-green-800/10 backdrop-blur-md'}
      ${scrolled ? 'shadow-md' : ''}`}>
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-[70px]">

        {/* Logo */}
        <Link
          to="/"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex items-center gap-2.5 font-serif font-bold text-lg"
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${dark ? 'bg-green-600' : 'bg-green-800'}`}>
            <img src="/gloobal exclusive ariel farm logo.png" alt="GEA Farm Logo" className="w-6 h-6" />
          </div>
          <span className={dark ? 'text-green-400' : 'text-green-800'}>GEA Farm</span>
        </Link>

        {/* Desktop Links */}
        <ul className="hidden md:flex gap-8 list-none">
          {NAV_LINKS.map(l => (
            <li key={l.label}>
              <button
                onClick={() => handleLink(l)}
                className={`relative text-sm font-medium tracking-wide transition-colors duration-200 nav-link-underline
                  ${isActivePage(l)
                    ? dark ? 'text-green-400' : 'text-green-600'
                    : dark ? 'text-green-300 hover:text-green-400' : 'text-green-700 hover:text-green-600'}`}
              >
                {l.label}
                {isActivePage(l) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-green-600 rounded-full" />
                )}
              </button>
            </li>
          ))}
        </ul>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition-all duration-200
              ${dark ? 'border-green-400/20 text-green-300 hover:bg-green-800' : 'border-green-800/15 text-green-700 hover:bg-green-50'}`}
          >
            <span>{dark ? '☀️' : '🌙'}</span>
            <span className="hidden sm:inline">{dark ? 'Light' : 'Dark'}</span>
          </button>
          <button
            onClick={handleContact}
            className="bg-green-700 hover:bg-green-800 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-all duration-200 hover:-translate-y-0.5"
          >
            Contact Us
          </button>
          <button
            className="md:hidden p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <div className={`w-5 h-0.5 mb-1 transition-all ${dark ? 'bg-green-300' : 'bg-green-800'} ${menuOpen ? 'rotate-45 translate-y-1.5' : ''}`} />
            <div className={`w-5 h-0.5 mb-1 transition-all ${dark ? 'bg-green-300' : 'bg-green-800'} ${menuOpen ? 'opacity-0' : ''}`} />
            <div className={`w-5 h-0.5 transition-all ${dark ? 'bg-green-300' : 'bg-green-800'} ${menuOpen ? '-rotate-45 -translate-y-1.5' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className={`md:hidden border-t px-6 py-4 flex flex-col gap-3
          ${dark ? 'bg-green-900 border-green-400/10' : 'bg-white border-green-800/10'}`}>
          {NAV_LINKS.map(l => (
            <button
              key={l.label}
              onClick={() => handleLink(l)}
              className={`text-sm font-medium text-left py-2 transition-colors
                ${isActivePage(l)
                  ? dark ? 'text-green-400' : 'text-green-600'
                  : dark ? 'text-green-300' : 'text-green-700'}`}
            >
              {l.label}
              {isActivePage(l) && (
                <span className="block h-0.5 w-8 mt-0.5 bg-green-600 rounded-full" />
              )}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}